import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabase'
import { Spinner } from '../components/ui/Spinner'

const PREFS = [
  { key: 'weekly_digest',       label: 'Weekly digest',           description: 'Monday morning summary of your upcoming week' },
  { key: 'event_reminders',     label: 'Event reminders',         description: 'Notifications for calendar events' },
  { key: 'task_reminders',      label: 'Task reminders',          description: 'Reminders for upcoming due dates' },
  { key: 'bill_reminders',      label: 'Bill reminders',          description: 'Alerts when bills are coming due' },
  { key: 'vehicle_reminders',   label: 'Vehicle reminders',       description: 'Service and maintenance reminders' },
  { key: 'medication_reminders', label: 'Medication reminders',   description: 'Refill and dose reminders' },
  { key: 'custody_reminders',   label: 'Custody reminders',       description: 'Handoff and schedule notifications' },
  { key: 'marketing',           label: 'Product updates',         description: 'New features, tips, and occasional offers' },
]

const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
      checked ? 'bg-primary' : 'bg-border'
    }`}
  >
    <span
      className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform transition duration-200 ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`}
    />
  </button>
)

export default function NotificationsSettings() {
  const { user } = useAuth()
  const [prefs, setPrefs] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState({})

  useEffect(() => {
    if (!user?.id) return
    supabase
      .from('email_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          setPrefs(data)
        } else {
          // Default all to true if no row yet
          const defaults = Object.fromEntries(PREFS.map((p) => [p.key, true]))
          setPrefs({ user_id: user.id, ...defaults })
        }
        setLoading(false)
      })
  }, [user?.id])

  const handleToggle = async (key, value) => {
    const optimistic = { ...prefs, [key]: value }
    setPrefs(optimistic)
    setSaving((s) => ({ ...s, [key]: true }))

    try {
      await supabase
        .from('email_preferences')
        .upsert({ user_id: user.id, ...optimistic }, { onConflict: 'user_id' })
    } catch {
      // Revert on error
      setPrefs((p) => ({ ...p, [key]: !value }))
    } finally {
      setSaving((s) => ({ ...s, [key]: false }))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/settings" className="text-text-secondary hover:text-text-primary">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </Link>
        <h1 className="text-xl font-bold text-text-primary">Notifications</h1>
      </div>

      <section className="bg-surface rounded-base border border-border overflow-hidden mb-6">
        <div className="px-5 py-3 border-b border-border bg-background">
          <p className="text-xs font-semibold text-text-muted uppercase tracking-wide">Email notifications</p>
        </div>
        {PREFS.map((pref, i) => (
          <div
            key={pref.key}
            className={`flex items-center justify-between px-5 py-4 ${
              i < PREFS.length - 1 ? 'border-b border-border' : ''
            }`}
          >
            <div className="flex-1 min-w-0 pr-4">
              <p className="text-sm font-medium text-text-primary">{pref.label}</p>
              <p className="text-xs text-text-secondary mt-0.5">{pref.description}</p>
            </div>
            {saving[pref.key] ? (
              <Spinner size="sm" />
            ) : (
              <Toggle
                checked={prefs?.[pref.key] ?? true}
                onChange={(val) => handleToggle(pref.key, val)}
              />
            )}
          </div>
        ))}
      </section>

      <p className="text-xs text-text-muted text-center">
        Turning off all emails won't stop account security and billing notifications.
      </p>
    </div>
  )
}
